'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueries } from '@tanstack/react-query';
import { useCallback, useContext, useMemo, useRef, useSyncExternalStore } from 'react';
import { useGetMarketConvictionChart } from '../../generated/hooks/useGetMarketConvictionChart.gen.js';
import { useGetMarketConvictionChartSpec } from '../../generated/specs/markets.spec.gen.js';
import { mergeConvictionChartSnapshots } from '../../helpers/conviction-chart.js';
import { convictionChartRecoveryStart, planConvictionChartPages } from '../../helpers/conviction-chart-request.js';
import { RegistryContext } from '../context/RegistryContext.js';
/**
 * Shared sparse-history owner. Closed pages never subscribe or reconnect-fetch;
 * only the newest page takes one post-wire snapshot per wire generation.
 */
export function useAuthoritativeConvictionChart(args) {
  const enabled = args.enabled ?? true;
  const limit = args.limit ?? 360;
  const wireVersion = useWireInterestVersion();
  const selectionKey = [args.pubkey, args.selectionKey ?? `${args.interval}:${args.from}:${args.head}`, args.to ?? '', String(limit)].join(':');
  const planRef = useRef({
    key: selectionKey,
    interval: args.interval,
    from: args.from,
    head: args.head
  });
  if (planRef.current.key !== selectionKey) {
    planRef.current = {
      key: selectionKey,
      interval: args.interval,
      from: args.from,
      head: args.head
    };
  }
  const plan = planRef.current;
  const planningHead = new Date(Date.parse(plan.head) + (args.to === undefined ? 1 : 0)).toISOString();
  const pages = useMemo(() => planConvictionChartPages({
    from: plan.from,
    to: args.to ?? planningHead,
    interval: plan.interval,
    limit
  }), [plan, args.to, limit, planningHead]);
  const initialLatestPage = pages.at(-1) ?? {
    from: plan.from,
    to: args.to ?? planningHead
  };
  const latestRef = useRef({
    key: selectionKey,
    wireVersion,
    from: initialLatestPage.from,
    monotonicHeadMs: globalThis.performance.now()
  });
  if (latestRef.current.key !== selectionKey) {
    latestRef.current = {
      key: selectionKey,
      wireVersion,
      from: initialLatestPage.from,
      monotonicHeadMs: globalThis.performance.now()
    };
  } else if (latestRef.current.wireVersion !== wireVersion) {
    latestRef.current.wireVersion = wireVersion;
    if (args.to === undefined) {
      latestRef.current.from = convictionChartRecoveryStart({
        previousFrom: latestRef.current.from,
        estimatedServerNowMs: Date.parse(plan.head) + globalThis.performance.now() - latestRef.current.monotonicHeadMs,
        interval: plan.interval,
        limit
      });
    }
  }
  const latestPage = {
    ...initialLatestPage,
    from: latestRef.current.from
  };
  const retainedRef = useRef({
    key: selectionKey,
    latestFrom: latestPage.from,
    complete: null,
    older: null
  });
  if (retainedRef.current.key !== selectionKey) {
    retainedRef.current = {
      key: selectionKey,
      latestFrom: latestPage.from,
      complete: null,
      older: null
    };
  } else if (retainedRef.current.latestFrom !== latestPage.from) {
    retainedRef.current.older = retainedRef.current.complete ?? retainedRef.current.older;
    retainedRef.current.complete = null;
    retainedRef.current.latestFrom = latestPage.from;
  }
  const historyPages = pages.slice(0, -1);
  const history = useQueries({
    queries: historyPages.map(page => ({
      ...queryOptions(args, plan, page, limit),
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false
    })),
    combine: combineHistoryQueries
  });
  const latestArgs = latestQueryArgs(args, plan, latestPage, limit);
  const latest = useGetMarketConvictionChart(latestArgs, {
    enabled,
    eagerSnapshot: false,
    fetchOnce: true,
    singleSnapshot: true,
    retry: false
  });
  const older = retainedRef.current.older;
  // Placeholder data belongs to the previous query key, which may use another
  // interval; merging it with this selection's history breaks the page contract.
  const latestPending = latest.isPlaceholderData;
  const data = useMemo(() => {
    const latestSnapshot = latestPending ? undefined : latest.data;
    if (latestSnapshot === null) return null;
    if (latestSnapshot === undefined) return older ?? undefined;
    if (older === null && history.data.some(snapshot => snapshot === undefined)) {
      return undefined;
    }
    const snapshots = older === null ? [] : [older];
    if (older === null) {
      for (const snapshot_0 of history.data) {
        if (snapshot_0 !== null && snapshot_0 !== undefined) snapshots.push(snapshot_0);
      }
    }
    snapshots.push(latestSnapshot);
    return mergeConvictionChartSnapshots(snapshots);
  }, [history.data, latest.data, latestPending, older]);
  if (data !== null && data !== undefined) retainedRef.current.complete = data;
  const refetch = useCallback(async () => {
    await Promise.all([...history.refetches.map(run => run()), latest.refetch()]);
  }, [history.refetches, latest.refetch]);
  return {
    data,
    error: history.error ?? latest.error,
    isFetching: latest.isFetching || history.isFetching,
    isLoading: latest.isLoading || latestPending || history.isLoading,
    refetch
  };
}
function useWireInterestVersion() {
  const $ = _c(4);
  const registry = useContext(RegistryContext);
  let t0;
  if ($[0] !== registry) {
    t0 = listener => registry?.subscribeWireInterestVersion?.(listener) ?? _temp;
    $[0] = registry;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribe = t0;
  let t1;
  if ($[2] !== registry) {
    t1 = () => registry?.getWireInterestVersion?.() ?? 0;
    $[2] = registry;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const read = t1;
  return useSyncExternalStore(subscribe, read, read);
}
function _temp() {}
function combineHistoryQueries(results) {
  return {
    data: results.map(result => result.data),
    error: results.find(result => result.error !== null)?.error ?? null,
    isFetching: results.some(result => result.isFetching),
    isLoading: results.some(result => result.isLoading),
    refetches: results.map(result => result.refetch)
  };
}
function queryOptions(args, plan, page, limit) {
  const detail = useGetMarketConvictionChartSpec.entityKeys.detail;
  if (detail === undefined) throw new Error('Missing conviction chart detail query');
  return detail({
    pubkey: args.pubkey,
    interval: plan.interval,
    from: page.from,
    to: page.to,
    limit
  });
}
function latestQueryArgs(args, plan, page, limit) {
  return {
    pubkey: args.pubkey,
    interval: plan.interval,
    from: page.from,
    ...(args.to === undefined ? {} : {
      to: page.to
    }),
    limit
  };
}